import {
  createCsrfMiddleware,
  createMiddleware,
  createStart,
} from '@tanstack/react-start'
import { auth } from '@server/auth'
import { getAccessRequirement } from '@server/access-policy'
import { buildAccessDenyLog, logAccessDenied } from '@server/access-logging'
import { env } from '@server/env'

const accessMiddleware = createMiddleware().server(
  async ({ next, request }) => {
    if (request.method === 'OPTIONS') {
      return next()
    }

    const url = new URL(request.url)
    const requirement = getAccessRequirement(url.pathname)

    if (requirement === 'public') {
      return next()
    }

    if (requirement === 'deny') {
      logAccessDenied(
        buildAccessDenyLog({
          request,
          requirement,
          reason: 'api_denied',
        }),
      )
      return new Response('Not Found', { status: 404 })
    }

    const session = await auth.api.getSession({ headers: request.headers })

    if (!session?.user) {
      logAccessDenied(
        buildAccessDenyLog({
          request,
          requirement,
          reason: 'unauthenticated',
          session,
        }),
      )
      return requirement === 'auth' ? redirectToLogin(url) : redirectHome(url)
    }

    if (requirement === 'auth') {
      return next()
    }

    const adminUserIds = (env.ADMIN_USER_IDS || '').split(',').filter(Boolean)
    if (adminUserIds.includes(session.user.id)) {
      return next()
    }

    const permissionResult = await auth.api.hasPermission({
      headers: request.headers,
      body: {
        permissions: {
          user: ['list'],
        },
      },
    })

    const hasAdminPermission =
      permissionResult === true || permissionResult.hasPermission === true

    if (!hasAdminPermission) {
      logAccessDenied(
        buildAccessDenyLog({
          request,
          requirement,
          reason: 'unauthorized',
          session,
        }),
      )
      return redirectHome(url)
    }

    return next()
  },
)

const UNSAFE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE'])

function isDurableChatCancellationPath(pathname: string) {
  return /^\/api\/chat-generations\/[^/]+\/cancel$/.test(pathname)
}

export function shouldApplyCsrfProtection(ctx: {
  handlerType: string
  request: Request
}) {
  if (ctx.handlerType === 'serverFn') return true
  if (ctx.handlerType !== 'router') return false

  const url = new URL(ctx.request.url)
  if (!UNSAFE_METHODS.has(ctx.request.method.toUpperCase())) return false

  return (
    url.pathname === '/api/stream-chat' ||
    isDurableChatCancellationPath(url.pathname)
  )
}

const csrfMiddleware = createCsrfMiddleware({
  filter: shouldApplyCsrfProtection,
})

export const startInstance = createStart(() => ({
  requestMiddleware: [csrfMiddleware, accessMiddleware],
}))

const redirectToLogin = (url: URL) => {
  const loginUrl = new URL('/login', url.origin)
  loginUrl.searchParams.set('redirect', `${url.pathname}${url.search}`)
  return Response.redirect(loginUrl.toString(), 302)
}

const redirectHome = (url: URL) => {
  const homeUrl = new URL('/', url.origin)
  return Response.redirect(homeUrl.toString(), 302)
}
